/**
 * PulseCast composer — the real one.
 *
 * Publishes to `POST /api/pulse` with per-site destination fan-out.
 * Debounced call to `/api/pulse/suggest` recommends destinations as the
 * user types. Composer stays collapsed until the textarea gets focus.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Image as ImageIcon, Video as VideoIcon, Newspaper, Calendar,
  BarChart2, Send, X, Sparkles, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DESTINATIONS = [
  { slug: "socialpulse", label: "My SocialPulse", color: "#7C3AED", locked: true },
  { slug: "nature",      label: "NaturePulse",     color: "#10B981" },
  { slug: "farmers",     label: "FarmersPulse",    color: "#F59E0B" },
  { slug: "food",        label: "FoodPulse",       color: "#EF4444" },
  { slug: "sellnbuy",    label: "SellnBuy",        color: "#1F6FEB" },
  { slug: "photo",       label: "PhotoPulse",      color: "#EC4899" },
  { slug: "traveler",    label: "TravelerPulse",   color: "#0EA5E9" },
  { slug: "alumni",      label: "AlumniPulse",     color: "#8B5CF6" },
];


export default function PulseComposer({ me, onPublished }) {
  const [open, setOpen]     = useState(false);
  const [text, setText]     = useState("");
  const [dests, setDests]   = useState(new Set(["socialpulse"]));
  const [reasons, setReasons] = useState({});
  const [busy, setBusy]     = useState(false);
  const dbRef = useRef(null);

  const toggleDest = (slug) => {
    if (slug === "socialpulse") return;              // always on
    setDests((prev) => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });
  };

  // Debounced suggestion.
  const runSuggest = useCallback(async (t) => {
    if (t.trim().length < 8) return;
    try {
      const r = await axios.post(`${API}/pulse/suggest`, { text: t });
      const suggested = new Set(r.data.destinations || []);
      setDests((prev) => {
        const next = new Set(prev);
        suggested.forEach((s) => next.add(s));
        return next;
      });
      setReasons(r.data.reason || {});
    } catch {}
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(dbRef.current);
    dbRef.current = setTimeout(() => runSuggest(text), 500);
    return () => clearTimeout(dbRef.current);
  }, [text, open, runSuggest]);

  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/pulse`,
        { text: text.trim(), kind: "post",
          destinations: Array.from(dests) },
        { withCredentials: true },
      );
      toast.success("Posted");
      setText(""); setDests(new Set(["socialpulse"])); setReasons({});
      setOpen(false);
      onPublished?.(r.data.post);
    } catch (e) {
      toast.error("Could not post");
    } finally { setBusy(false); }
  };

  const avatar = me?.avatar_url || me?.profile_picture
    || `https://api.dicebear.com/7.x/initials/svg?seed=${me?.handle || me?.name || "?"}`;

  if (!open) {
    return (
      <section data-testid="composer-stub"
               className="rounded-xl bg-white border p-4"
               style={{ borderColor: "#E5E7EB" }}>
        <div className="flex items-center gap-3">
          <img src={avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
          <button
            data-testid="composer-open-btn"
            onClick={() => setOpen(true)}
            className="flex-1 text-left rounded-full px-4 py-2.5 text-[13.5px]"
            style={{ background: "#F1F5F9", color: "#64748B" }}
          >
            What's on your mind today?
          </button>
        </div>
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-1 pt-2 border-t"
             style={{ borderColor: "#F1F5F9" }}>
          {[
            { icon: ImageIcon, label: "Photo",  color: "#10B981" },
            { icon: VideoIcon, label: "Video",  color: "#EF4444" },
            { icon: Newspaper, label: "Article",color: "#3B82F6" },
            { icon: Calendar,  label: "Event",  color: "#F59E0B" },
            { icon: BarChart2, label: "Poll",   color: "#7C3AED" },
            { icon: Send,      label: "Publish",color: "#64748B" },
          ].map((a) => (
            <button key={a.label} onClick={() => setOpen(true)}
                    className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12.5px] hover:bg-slate-50"
                    style={{ color: "#475569" }}>
              <a.icon className="w-3.5 h-3.5" style={{ color: a.color }} />
              {a.label}
            </button>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section data-testid="composer-open"
             className="rounded-xl bg-white border p-4 shadow-sm"
             style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-start gap-3">
        <img src={avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
        <div className="flex-1 min-w-0">
          <textarea
            data-testid="composer-textarea"
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Share something with your communities…"
            rows={4}
            className="w-full text-[15px] leading-[1.55] outline-none resize-none"
            style={{ color: "#0F172A" }}
          />
        </div>
        <button
          data-testid="composer-close-btn"
          onClick={() => { setOpen(false); setText(""); }}
          className="w-8 h-8 rounded-full inline-flex items-center justify-center hover:bg-slate-100"
          style={{ color: "#64748B" }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-3 pt-3 border-t" style={{ borderColor: "#F1F5F9" }}>
        <div className="text-[11px] font-mono uppercase tracking-[0.15em] mb-2 flex items-center gap-1.5"
             style={{ color: "#64748B" }}>
          <Sparkles className="w-3 h-3" style={{ color: "#7C3AED" }} />
          Publish to · PulseCast
        </div>
        <div className="flex flex-wrap gap-1.5">
          {DESTINATIONS.map((d) => {
            const on = dests.has(d.slug);
            return (
              <button
                key={d.slug}
                data-testid={`dest-${d.slug}`}
                onClick={() => toggleDest(d.slug)}
                disabled={d.locked}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] font-medium border transition"
                style={{
                  background:  on ? d.color : "white",
                  color:       on ? "white" : "#334155",
                  borderColor: on ? d.color : "#CBD5E1",
                  opacity:     d.locked ? 0.85 : 1,
                }}
                title={reasons[d.slug] ? `Suggested: matched "${reasons[d.slug]}"` : ""}
              >
                {d.label}
                {reasons[d.slug] && !d.locked && (
                  <Sparkles className="w-3 h-3" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-[11.5px] opacity-60">
          {text.trim().length}/8000
        </div>
        <button
          data-testid="composer-publish-btn"
          onClick={submit}
          disabled={busy || !text.trim()}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-[13.5px] font-semibold text-white disabled:opacity-50"
          style={{ background: "#7C3AED" }}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Publish
        </button>
      </div>
    </section>
  );
}
